import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Stack,
  TextInput,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import { ChangeEvent, useRef, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FACT_AGGREATION_METHOD } from '@/api/rest/resources/types/fact';
import {
  INDICATOR,
  INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS,
  INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE,
  PatchDashboardIndicatorProductSubscriptionConfigurationBodyData,
  PostDashboardIndicatorProductSubscriptionConfigurationBodyData,
} from '@/api/rest/resources/types/IndicatorProduct';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useGetIndicatorProductSubscriptions } from '@/hooks/useDashboardIndicatorProductSubscription';
import {
  useCreateIndicatorProductSubscriptionConfiguration,
  useGetIndicatorProductSubscriptionConfigurations,
  useUpdateIndicatorProductSubscriptionConfiguration,
} from '@/hooks/useDashboardIndicatorProductSubscriptionConfiguration';
import { useFileUpload } from '@/hooks/useFileUpload';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import {
  DEFAULT_FORM_VALUES,
  FORM_FIELDS,
  InSituDataControlsFormFieldValues,
  useControlsFormInSituData,
} from '@/pages/shared/uploads/pages/hooks/useFormControlsInSituDataConfig';
import { FILE_UPLOAD_VALIDATION } from '@/pages/shared/uploads/pages/hooks/useFormControlsInSituFileUpload';
import { isNullish } from '@/utils/booleans';

export const AddNewConfig = ({
  insituIpSubscriptionIdArg,
  asIcon = false,
  type = INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE.DEFAULT,
}: {
  insituIpSubscriptionIdArg?: string;
  asIcon?: boolean;
  type?: (typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE)[keyof typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE];
}) => {
  const isStaffUSer = useIsStaffUser();
  const [openDialog, setOpenDialog] = useState(false);
  const ipSubscriptions = useGetIndicatorProductSubscriptions().data?.results ?? [];
  const insituIpSubscription = ipSubscriptions.find((sub) => sub.indicator_product.name === INDICATOR.IN_SITU_PACK);
  const insituIpSubscriptionId = insituIpSubscriptionIdArg ?? insituIpSubscription?.id;

  if (isNullish(insituIpSubscription) || !insituIpSubscriptionId) {
    return null;
  }

  if (!isStaffUSer) {
    return null;
  }

  const handleDialogOpenChange = () => setOpenDialog((prev) => !prev);

  return (
    <>
      <AddNewConfigButton onClick={handleDialogOpenChange} asIcon={asIcon} type={type} />
      {openDialog && (
        <AddNewConfigDialog
          data-testid='add-new-config-dialog'
          open
          onOpenChange={handleDialogOpenChange}
          insituIpSubscriptionId={insituIpSubscriptionId}
          type={type}
        />
      )}
    </>
  );
};

const AddNewConfigButton = ({
  onClick,
  asIcon = false,
  type,
}: {
  onClick: () => void;
  asIcon: boolean;
  type: (typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE)[keyof typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE];
}) => {
  const { t } = useTranslation();

  if (asIcon) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            data-testid='config-card-config-button'
            onClick={onClick}
            className='aspect-square py-1 px-2 rounded-full'
            variant='text'
          >
            <Icon icon='ri-settings-3-line' className='text-xl' />
          </Button>
        </TooltipTrigger>
        <TooltipContent className='whitespace-pre-wrap'>
          <Stack className='gap-4'>{t('shared.insituDataUpload.newConfig.button.config')}</Stack>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Button
      data-testid='add-new-config-button'
      className='rounded-3xl border border-neutral-black-12 p-6 transition-all duration-200 gap-2 w-[380px] h-[320px] hover:border-primary-hover hover:bg-neutral-hover text-neutral-black-40'
      variant='text'
      onClick={onClick}
    >
      <Stack spacing={4} center centerMain>
        <Stack center centerMain>
          <Icon icon='ri-function-add-line' className='text-7xl' />
        </Stack>
        <Stack spacing={2}>
          <span className='typography-h4 whitespace-pre-wrap text-neutral-black-80'>
            {type === INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE.MEDIA_MAP
              ? t('shared.mediaMaps.newConfig.labels.addCustomIp')
              : t('shared.insituDataUpload.newConfig.labels.addCustomIp')}
          </span>
        </Stack>
      </Stack>
    </Button>
  );
};

const AddNewConfigDialog = ({
  open,
  onOpenChange,
  insituIpSubscriptionId,
  type,
}: DialogProps & {
  insituIpSubscriptionId: string;
  type: (typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE)[keyof typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE];
}) => {
  const { t } = useTranslation();

  return (
    <Dialog data-testid='add-new-config-dialog' open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[80vh] min-w-[40vw]'>
        <DialogHeader>
          <DialogTitle>{t('shared.insituDataUpload.newConfig.labels.addCustomIp')}</DialogTitle>
          <DialogBody className='overflow-y-auto'>
            <AddNewConfigForm insituIpSubscriptionId={insituIpSubscriptionId} onSubmit={onOpenChange} type={type} />
          </DialogBody>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};

const AddNewConfigForm = ({
  insituIpSubscriptionId,
  onSubmit,
  type,
}: {
  insituIpSubscriptionId: string;
  onSubmit: Pick<DialogProps, 'onOpenChange'>['onOpenChange'];
  type: (typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE)[keyof typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE];
}) => {
  const { t } = useTranslation();
  const inSituConfigurations =
    useGetIndicatorProductSubscriptionConfigurations(undefined, true, {
      type: INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE.DEFAULT,
    }).data?.results ?? [];
  const { submit: submitFileUpload, isSubmitting: isFileUploadSubmitting } = useFileUpload();

  const ipSubscriptionConfig = inSituConfigurations.find((sub) => sub.id === insituIpSubscriptionId);

  const isUpdateForm = !isNullish(ipSubscriptionConfig);
  const form = useControlsFormInSituData(
    isUpdateForm
      ? {
          defaultValues: {
            [FORM_FIELDS.INDICATOR_NAME]: ipSubscriptionConfig.indicator_name,
            [FORM_FIELDS.AGGREGATION_METHOD]: ipSubscriptionConfig.aggregation_method,
          },
        }
      : {
          defaultValues: DEFAULT_FORM_VALUES[`${type}`],
        },
  );

  const templateRef = useRef<HTMLInputElement>(null);
  const isMediaMapForm = type === INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE.MEDIA_MAP;

  const { submit: createSubmit, isSubmitting: isSubmittingCreate } =
    useCreateIndicatorProductSubscriptionConfiguration();
  const { submit: updateSubmit, isSubmitting: isSubmittingUpdate } =
    useUpdateIndicatorProductSubscriptionConfiguration();
  const isSubmitting = isSubmittingCreate || isSubmittingUpdate || isFileUploadSubmitting;

  const handleFormSubmit: SubmitHandler<InSituDataControlsFormFieldValues> = async (values) => {
    const missingFields =
      !values[FORM_FIELDS.INDICATOR_NAME] || (!values[FORM_FIELDS.AGGREGATION_METHOD] && !isMediaMapForm);

    if (missingFields) {
      return;
    }
    let templateFileInfoId;

    if (!isMediaMapForm) {
      const templateFile = values[FORM_FIELDS.TEMPLATE] as File | null;
      const templateExtension = templateFile?.name.split('.').pop()?.toLowerCase();
      if (
        templateFile &&
        !(FILE_UPLOAD_VALIDATION.ACCEPTED_FILE_TYPES as unknown as string[]).includes(`.${templateExtension}`)
      ) {
        return;
      }

      if (templateFile) {
        const response = await submitFileUpload(templateFile);
        templateFileInfoId = response.file_info;
      }
    }

    if (isUpdateForm) {
      const bodyData = new FormData();
      bodyData.append('indicator_name', values[FORM_FIELDS.INDICATOR_NAME]);
      bodyData.append('aggregation_method', values[FORM_FIELDS.AGGREGATION_METHOD]);

      if (templateFileInfoId) {
        bodyData.append('template', templateFileInfoId);
      }

      await updateSubmit(
        ipSubscriptionConfig.id,
        bodyData as unknown as PatchDashboardIndicatorProductSubscriptionConfigurationBodyData,
      );
    } else {
      const bodyData: Partial<PostDashboardIndicatorProductSubscriptionConfigurationBodyData> = {
        indicator_name: values[FORM_FIELDS.INDICATOR_NAME],
        aggregation_method: values[FORM_FIELDS.AGGREGATION_METHOD],
        status: INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.WAITING,
        indicator_product_subscription: insituIpSubscriptionId,
        template: templateFileInfoId,
        type,
      };

      await createSubmit(bodyData);
    }

    onSubmit?.(false);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newFile = e?.target?.files?.[0];
    if (newFile) {
      form.setValue(FORM_FIELDS.TEMPLATE, newFile);
    }
  };

  const requiredLabel = t('global.ui.form.input.required');

  return (
    <Form {...form}>
      <form data-testid='add-new-config-form' onSubmit={form.handleSubmit(handleFormSubmit)}>
        <Stack spacing={4}>
          <span className='typography-body1'>{t('shared.insituDataUpload.newConfig.description')}</span>
          <FormField
            control={form.control}
            name={FORM_FIELDS.INDICATOR_NAME}
            rules={{ required: requiredLabel }}
            render={({ field }) => (
              <FormItem className='flex flex-col gap-1'>
                <FormLabel>{t('shared.insituDataUpload.newConfig.fields.indicatorName.label')} *</FormLabel>
                <FormControl>
                  <TextInput
                    data-testid='indicator-name-input'
                    {...field}
                    placeholder={t('shared.insituDataUpload.newConfig.fields.indicatorName.placeholder')}
                    inputProps={{ maxLength: 255, minLength: 1 }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {!isMediaMapForm && (
            <FormField
              control={form.control}
              name={FORM_FIELDS.AGGREGATION_METHOD}
              rules={{ required: requiredLabel }}
              render={({ field: { value, onChange, ...field } }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormLabel>{t('shared.insituDataUpload.newConfig.fields.aggregationMethod.label')} *</FormLabel>
                  <Select value={value} onValueChange={onChange} {...field}>
                    <FormControl>
                      <SelectTrigger data-testid='aggregation-method-select' />
                    </FormControl>
                    <SelectContent>
                      {Object.values(FACT_AGGREATION_METHOD).map((method) => (
                        <SelectItem key={method} value={method} data-testid={`aggregation-method-option-${method}`}>
                          {t(`global.aggregationMethod.${method}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          {!isMediaMapForm && (
            <FormField
              control={form.control}
              name={FORM_FIELDS.TEMPLATE}
              render={({ field: { value } }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormLabel>{t('shared.insituDataUpload.newConfig.fields.template.label')}</FormLabel>
                  <FormControl className='flex-1'>
                    <div className='flex items-center gap-3'>
                      <input
                        ref={templateRef}
                        type='file'
                        data-testid='file-input'
                        accept={FILE_UPLOAD_VALIDATION.ACCEPTED_FILE_TYPES.join(',')}
                        onChange={handleFileChange}
                        className='hidden'
                      />
                      <Button type='button' variant='outline' onClick={() => templateRef.current?.click()}>
                        {t('shared.insituDataUpload.fileUpload.fields.selectFile')}
                      </Button>

                      {value && (
                        <span className='typography-body3 text-neutral-black-60'>
                          {typeof value !== 'string' ? value.name : value}
                        </span>
                      )}
                      {ipSubscriptionConfig?.template_url && !value && (
                        <span className='my-1'>
                          <a
                            href={ipSubscriptionConfig.template_url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='w-fit typography-body text-primary-100 hover:text-primary-hover flex items-center gap-1'
                          >
                            <span className='underline'>
                              {t('shared.insituDataUpload.newConfig.fields.template.alreadyExists')}
                            </span>
                            <Icon icon='ri-download-cloud-2-line' className='text-lg' />
                          </a>
                        </span>
                      )}
                    </div>
                  </FormControl>
                  <span className='typography-body2 text-neutral-black-60'>
                    {t('shared.insituDataUpload.newConfig.fields.template.placeholder')}
                  </span>
                </FormItem>
              )}
            />
          )}
          <Stack direction='row' className='justify-end'>
            <Button
              type='submit'
              data-testid='submit-button'
              variant='contained'
              className='w-fit'
              disabled={!form.formState.isValid || isSubmitting}
              loading={isSubmitting}
            >
              {t('global.ui.buttons.submit')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Form>
  );
};
